"use client"

import React, { useState, useEffect } from 'react';
import Loading from "../../../../components/Loading/loading";
import { CloseOutlined, SearchIcon } from "../../utils/icons";
import videoGroupServices from "../../utils/services/videoGroup";

interface Group {
  id: number;
  thumbnail: string;
  title?: string;
  is_show: number;
  parent: string;
  index: number;
}

interface PopupGroupViewProps {
  onClose: () => void;
  onSelect: (selectedGroup: Group) => void;
  language_id?: string;
  idPath: string;
}

const PopupGroupView: React.FC<PopupGroupViewProps> = ({
    onClose,
    onSelect,
    idPath
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
    const [groups, setGroups] = useState<Group[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingData, setIsLoadingData] = useState(true);

    // Fetch groups data on component mount
    useEffect(() => {
        fetchGroups();
    }, []);

    const fetchGroups = async () => {
        setIsLoadingData(true);
        try {
            const response = await videoGroupServices.getAllGroupWithoutVideoGroup(idPath) as any;
            if ( response.data.success && response.data.data) {
                setGroups(response.data.data);
            } else {
                console.error('Failed to fetch groups:', response);
                setGroups([]);
            }
        } catch (error) {
            console.error('Error fetching groups:', error);
            setGroups([]);
        } finally {
            setIsLoadingData(false);
        }
    };

    // Filter groups based on search term
    const filteredGroups = groups.filter(group => {
        const displayTitle = group.title || `Group_${group.id}`;
        return displayTitle.toLowerCase().includes(searchTerm.toLowerCase());
    });

    // Handle group selection
    const handleGroupSelection = (group: Group) => {
        setSelectedGroup(group);
    };

    // Handle the search input change
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    // Handle select button click
    const handleSelectGroup = async () => {
        if (!selectedGroup) {
            alert('Vui lòng chọn một nhóm cảnh');
            return;
        }

        setIsLoading(true);
        
        try {
            onSelect(selectedGroup);
            // The parent component handles closing the popup
        } catch (error) {
            console.error('Error selecting group:', error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/30 backdrop-blur-sm">
            <div className="w-[900px] max-w-[90vw] glass_background rounded-[16px] backdrop-blur-[80px] rounded-[20px] shadow-[0px_4px_48px_0px_rgba(0,0,0,0.2),inset_-1px_1px_3px_0px_rgba(255,255,255,0.8),inset_0px_4px_10px_3px_rgba(255,255,255,0.2)] p-6 flex flex-col gap-6 relative overflow-hidden"
                style={{
                    height: `calc(100dvh - 150px)`,
                    maxHeight: '80vh',
                }}>
                {/* Close button */}
                <button
                    className="absolute top-2 right-2 w-[64px] h-[64px] flex items-center justify-center"
                    onClick={onClose}
                >
                    <div className="w-[48px] h-[48px] rounded-[10px] flex items-center justify-center">
                        <CloseOutlined width="18" height="18" stroke="#464646" strokeWidth="2" />
                    </div>
                </button>

                {/* Title */}
                <h2 className="text-2xl font-bold font-['Roboto_Flex']">
                    Chọn nhóm cảnh
                </h2>

                {/* Search box */}
                <div className="flex items-center gap-4 bg-white rounded-[10px] border border-[#ECECEC] p-5 w-full">
                    <SearchIcon width="40" height="40" stroke="black" strokeWidth="2" />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        placeholder="Tìm kiếm nhóm cảnh..."
                        className="bg-transparent text-xl flex-1 outline-none text-[#B0B0B0]"
                    />
                </div>

                {/* List of groups */}
                <div className="bg-white rounded-2xl border border-[#ECECEC] p-3 flex-1 overflow-y-auto scrollbar-hide max-h-[500px]">
                    {isLoadingData ? (
                        <div className="flex flex-col items-center justify-center h-full gap-4 min-h-[200px]">
                            <Loading type="spinner" size="lg" color="#000000" />
                            <p className="text-lg text-gray-600 text-center">
                                Đang tải danh sách nhóm cảnh...
                            </p>
                        </div>
                    ) : groups.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full gap-4 min-h-[200px]">
                            <div className="text-gray-400 text-lg">📭</div>
                            <p className="text-lg text-gray-600 text-center">
                                Không có nhóm cảnh nào
                            </p>
                        </div>
                    ) : filteredGroups.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full gap-4 min-h-[200px]">
                            <div className="text-gray-400 text-lg">🔍</div>
                            <p className="text-lg text-gray-600 text-center">
                                Không tìm thấy nhóm cảnh nào với từ khóa "{searchTerm}"
                            </p>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-2.5">
                                            {filteredGroups.map((group) => {
                                                const displayTitle = group.title || `Group_${group.id}`;
                                                return (
                                <div
                                    key={group.id}
                                    className={`flex flex-row justify-between items-center p-2.5 rounded-[10px] cursor-pointer hover:bg-gray-50 ${
                                        selectedGroup?.id === group.id ? "bg-blue-100 border-2 border-blue-300" : "border-2 border-transparent"
                                    }`}
                                    onClick={() => handleGroupSelection(group)}
                                >
                                    <div className="flex flex-row items-center gap-6">
                                        <div className="w-16 h-16 bg-[#D9D9D9] border border-[#ECECEC] rounded-[10px] overflow-hidden relative">
                                            {group.thumbnail ? (
                                                <>
                                                    <img
                                                        src={group.thumbnail}
                                                        alt={displayTitle}
                                                        className="w-full h-full object-cover"
                                                        onError={(e) => {
                                                            (e.target as HTMLImageElement).style.display = 'none';
                                                            const fallback = (e.target as HTMLImageElement).nextElementSibling as HTMLElement;
                                                            if (fallback) fallback.style.display = 'flex';
                                                        }}
                                                    />
                                                    
                                                    <div className="absolute inset-0 w-full h-full bg-gradient-to-b from-[#464646] via-[rgba(101,101,101,0.5)] to-transparent flex items-center justify-center" style={{ display: 'none' }}>
                                                        <span className="text-white text-xs">Không có ảnh</span>
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="w-full h-full bg-gradient-to-b from-[#464646] via-[rgba(101,101,101,0.5)] to-transparent flex items-center justify-center">
                                                    <span className="text-white text-xs">Không có ảnh</span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex flex-col gap-1 flex-1 min-w-0">
                                            <span 
                                                className="text-lg font-medium truncate cursor-pointer leading-tight" 
                                                title={`Group: ${displayTitle}\nID: ${group.id}`}
                                            >
                                                {displayTitle}
                                            </span>
                                            {/* <span className="text-sm text-gray-500 truncate">
                                                {group.is_show ? 'Hiển thị' : 'Ẩn'}
                                            </span> */}
                                        </div>
                                    </div>
                                </div>
                                                );
                                            })}
                        </div>
                    )}
                </div>

                {/* Action buttons */}
                <div className="flex justify-between w-full gap-3">
                    <button
                        type="button"
                        onClick={handleSelectGroup}
                        disabled={isLoading || !selectedGroup}
                        className={`flex-1 bg-green-500 text-white rounded-[10px] p-2.5 flex items-center justify-center gap-2.5 ${
                            (isLoading || !selectedGroup) ? "opacity-50 cursor-not-allowed" : "hover:bg-green-600"
                        }`}
                    >
                        <span className="text-xl">
                            {isLoading ? (
                                <>
                                    <Loading type="spinner" size="sm" color="#FFFFFF" />
                                    <span className="ml-2">Đang chọn...</span>
                                </>
                            ) : (
                                "Chọn"
                            )}
                        </span>
                    </button>

                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isLoading}
                        className={`flex-1 bg-white border border-[#ECECEC] rounded-[10px] p-2.5 flex items-center justify-center ${
                            isLoading ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-50"
                        }`}
                    >
                        <span className="text-xl">Hủy bỏ</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PopupGroupView;
